import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { usersAPI, projectsAPI, jobCardsAPI, estimatesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import NotificationBell from './NotificationBell';
import {
  LogOut,
  Users,
  UserPlus,
  Building2,
  FolderKanban,
  FileText,
  Edit,
  Trash2,
  RefreshCw,
  Shield,
  Eye,
  EyeOff,
  Wrench,
  Activity
} from 'lucide-react';
import appLogo from '../assets/logo.png';
import MachineManagement from './MachineManagement';
import UsageReport from './UsageReport';

const ROLES = [
  { value: 'owner', label: 'Owner' },
  { value: 'admin', label: 'Admin' },
  { value: 'coordinator', label: 'Coordinator' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'machine_operator', label: 'Pressing Operator' },
  { value: 'cutting_operator', label: 'Cutting Operator' },
  { value: 'boring_operator', label: 'Boring Operator' },
  { value: 'accountant', label: 'Accountant' },
  { value: 'client', label: 'Client' }
];

const COUNTRY_CODES = [
  { value: '+91', label: '+91 (India)' },
  { value: '+1', label: '+1 (USA/Canada)' },
  { value: '+44', label: '+44 (UK)' },
  { value: '+971', label: '+971 (UAE)' },
  { value: '+65', label: '+65 (Singapore)' },
  { value: '+61', label: '+61 (Australia)' }
];

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [jobCards, setJobCards] = useState([]);
  const [estimates, setEstimates] = useState([]);
  
  // Dialog states
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: '',
    countryCode: '+91',
    mobile: '',
    password: '',
    role: 'coordinator',
    company: '',
    email: ''
  });
  const [showPasswords, setShowPasswords] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersData, projectsData, jobCardsData, estimatesData] = await Promise.all([
        usersAPI.getAdminView().catch(() => usersAPI.getAll()),
        projectsAPI.getAll(),
        jobCardsAPI.getAll(),
        estimatesAPI.getAll()
      ]);
      setUsers(usersData);
      setProjects(projectsData);
      setJobCards(jobCardsData);
      setEstimates(estimatesData);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setUserForm({
      name: '',
      countryCode: '+91',
      mobile: '',
      password: '',
      role: 'coordinator',
      company: '',
      email: ''
    });
    setShowUserDialog(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      name: user.name,
      countryCode: user.country_code || '+91',
      mobile: user.mobile || '',
      password: '',
      role: user.role,
      company: user.company || '',
      email: user.email || ''
    });
    setShowUserDialog(true);
  };

  const handleSubmitUser = async () => {
    try {
      const submitData = { ...userForm };
      // Store 10-digit mobile for login (strip any accidental prefix)
      submitData.mobile = submitData.mobile.replace(/^\+?91/, '').replace(/^0+/, '').trim();
      // Store country code separately for WhatsApp
      submitData.country_code = submitData.countryCode;
      delete submitData.countryCode;
      
      if (editingUser) {
        // Update user
        const updateData = { ...submitData };
        if (!updateData.password) delete updateData.password;
        await usersAPI.update(editingUser.id, updateData);
        toast({ title: "Success", description: "User updated successfully" });
      } else {
        // Create user
        if (!submitData.password) {
          toast({ title: "Error", description: "Password is required", variant: "destructive" });
          return;
        }
        await usersAPI.create(submitData);
        toast({ title: "Success", description: "User created successfully" });
      }
      setShowUserDialog(false);
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save user",
        variant: "destructive"
      });
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await usersAPI.delete(userId);
      toast({ title: "Success", description: "User deleted successfully" });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete user",
        variant: "destructive"
      });
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner': return 'bg-purple-100 text-purple-800';
      case 'admin': return 'bg-red-100 text-red-800';
      case 'coordinator': return 'bg-blue-100 text-blue-800';
      case 'supervisor': return 'bg-yellow-100 text-yellow-800';
      case 'accountant': return 'bg-green-100 text-green-800';
      case 'client': return 'bg-gray-100 text-gray-800';
      default: return 'bg-orange-100 text-orange-800';
    }
  };

  const groupUsersByRole = () => {
    const grouped = {};
    users.forEach(u => {
      if (!grouped[u.role]) grouped[u.role] = [];
      grouped[u.role].push(u);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const groupedUsers = groupUsersByRole();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center overflow-hidden">
              <img src={appLogo} alt="Interior Job Work" className="w-[70px] h-[70px] object-cover" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Interior Job Work Admin</h1>
              <p className="text-sm text-white/80">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <NotificationBell />
            <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{users.length}</p>
                  <p className="text-sm text-gray-500">Total Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{users.filter(u => u.role === 'client').length}</p>
                  <p className="text-sm text-gray-500">Clients</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <FolderKanban className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{projects.length}</p>
                  <p className="text-sm text-gray-500">Job Cards</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{estimates.length}</p>
                  <p className="text-sm text-gray-500">Estimates</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="users">
          <TabsList className="mb-4">
            <TabsTrigger value="users">
              <Users className="w-4 h-4 mr-2" />
              User Management
            </TabsTrigger>
            <TabsTrigger value="overview">
              <FolderKanban className="w-4 h-4 mr-2" />
              Data Overview
            </TabsTrigger>
            <TabsTrigger value="machines" data-testid="machines-tab">
              <Wrench className="w-4 h-4 mr-2" />
              Machines
            </TabsTrigger>
            <TabsTrigger value="usage" data-testid="usage-tab">
              <Activity className="w-4 h-4 mr-2" />
              Usage
            </TabsTrigger>
          </TabsList>

          {/* User Management Tab */}
          <TabsContent value="users" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">All Users</h2>
              <Button onClick={handleCreateUser}>
                <UserPlus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </div>

            {Object.entries(groupedUsers).map(([role, roleUsers]) => (
              <Card key={role}>
                <CardHeader className="py-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Badge className={getRoleBadgeColor(role)}>
                      {role.replace(/_/g, ' ').toUpperCase()}
                    </Badge>
                    <span className="text-gray-500 font-normal">({roleUsers.length})</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="divide-y">
                    {roleUsers.map(u => (
                      <div key={u.id} className="py-3 flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{u.name}</p>
                          <p className="text-sm text-gray-500">{u.mobile}</p>
                          {u.email && <p className="text-xs text-gray-400">{u.email}</p>}
                          {u.plain_password && (
                            <div className="flex items-center gap-1 mt-1">
                              <span className="text-xs text-gray-400">Password:</span>
                              <span className="text-xs font-mono bg-gray-100 px-1.5 py-0.5 rounded" data-testid={`user-password-${u.id}`}>
                                {showPasswords[u.id] ? u.plain_password : '••••••••'}
                              </span>
                              <button
                                onClick={() => setShowPasswords(prev => ({ ...prev, [u.id]: !prev[u.id] }))}
                                className="text-gray-400 hover:text-gray-600 p-0.5"
                                data-testid={`toggle-password-${u.id}`}
                              >
                                {showPasswords[u.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button variant="outline" size="sm" onClick={() => handleEditUser(u)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          {u.role !== 'owner' && u.role !== 'admin' && (
                            <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDeleteUser(u.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Data Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Recent Job Cards</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {projects.slice(0, 10).map(p => (
                      <div key={p.id} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div>
                          <p className="font-medium">{p.job_card_number || p.project_number}</p>
                          <p className="text-sm text-gray-500">{p.client_name}</p>
                        </div>
                        <Badge className={p.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}>
                          {p.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Recent Job Cards</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {jobCards.slice(0, 10).map(jc => (
                      <div key={jc.id} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div>
                          <p className="font-medium">{jc.job_card_number}</p>
                          <p className="text-sm text-gray-500">{jc.customer_name}</p>
                          {jc.created_by_name && (
                            <p className="text-xs text-blue-600">Coordinator: {jc.created_by_name}</p>
                          )}
                        </div>
                        <Badge className={jc.status === 'sent_to_accounts' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}>
                          {jc.status?.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Machines Tab */}
          <TabsContent value="machines" className="space-y-4">
            <MachineManagement />
          </TabsContent>

          {/* Usage Tab - logins and last seen (F5) */}
          <TabsContent value="usage" className="space-y-4">
            <UsageReport />
          </TabsContent>
        </Tabs>
      </div>

      {/* User Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Full name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Mobile Number *</Label>
              <Input
                type="tel"
                value={userForm.mobile}
                onChange={(e) => setUserForm(prev => ({ ...prev, mobile: e.target.value.replace(/\D/g, '') }))}
                placeholder="10-digit mobile number"
                maxLength={10}
              />
              <p className="text-xs text-gray-500">10-digit number used for login</p>
            </div>

            <div className="space-y-2">
              <Label>Country Code (for WhatsApp)</Label>
              <Select value={userForm.countryCode} onValueChange={(v) => setUserForm(prev => ({ ...prev, countryCode: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_CODES.map(cc => (
                    <SelectItem key={cc.value} value={cc.value}>{cc.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{editingUser ? 'New Password (leave blank to keep current)' : 'Password *'}</Label>
              <Input
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="********"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select value={userForm.role} onValueChange={(v) => setUserForm(prev => ({ ...prev, role: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Company</Label>
              <Input
                value={userForm.company}
                onChange={(e) => setUserForm(prev => ({ ...prev, company: e.target.value }))}
                placeholder="Company name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Email (optional)</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@example.com"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmitUser}>{editingUser ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
